// src/components/common/Navbar.tsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Box,
} from '@mui/material';
import {
  Menu as MenuIcon,
  KeyboardArrowLeftRounded,
  Logout,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ROUTES } from '../../constants';
import { UserRole } from '../../types';
import NavbarUserMenu from './NavbarUserMenu';
import NavbarMobileMenu from './NavbarMobileMenu';

interface NavbarProps {
  onMenuClick?: () => void;
  sidebarCollapsed?: boolean;
  showSidebar?: boolean;
}

const SIDEBAR_WIDTH_EXPANDED = 280;
const SIDEBAR_WIDTH_COLLAPSED = 72;

const navLinkSx = {
  color: 'rgb(75, 85, 99)',
  textTransform: 'none' as const,
  fontSize: '1rem',
  fontWeight: 'bold',
  fontFamily: 'Cairo, sans-serif',
  position: 'relative' as const,
  '&:hover': {
    color: '#6523d1',
    bgcolor: 'transparent',
  },
  '&::after': {
    content: '""',
    position: 'absolute' as const,
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: '2px',
    bgcolor: '#6523d1',
    transition: 'width 0.3s ease',
  },
  '&:hover::after': {
    width: '100%',
  },
};

const Navbar: React.FC<NavbarProps> = ({ onMenuClick, sidebarCollapsed = false, showSidebar = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isWaiter = user?.role === UserRole.WAITER;

  const isHomePage = location.pathname === ROUTES.HOME;

  const navbarWidth = showSidebar
    ? (sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED)
    : 0;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    handleMenuClose();
    navigate(ROUTES.LOGIN);
  };

  const handleDashboard = () => {
    if (user?.role === UserRole.ADMIN) {
      navigate(ROUTES.ADMIN_DASHBOARD);
    } else {
      navigate(ROUTES.RESTAURANT_DASHBOARD);
    }
    handleMenuClose();
  };

  const handleLogoClick = () => {
    if (isAuthenticated) {
      if (user?.role === UserRole.ADMIN) {
        navigate(ROUTES.ADMIN_DASHBOARD);
      } else {
        navigate(ROUTES.RESTAURANT_DASHBOARD);
      }
    } else {
      navigate(ROUTES.HOME);
    }
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        // Only hide on mobile for the public homepage (not logged in)
        display: { xs: (isHomePage && !isAuthenticated) ? 'none' : 'block', md: 'block' },
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(2px)',
        borderBottom: '2px solid rgba(209, 213, 219, 0.3)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        width: {
          xs: '100%',
          md: showSidebar ? `calc(100% - ${navbarWidth}px)` : '100%',
        },
        [isRTL ? 'mr' : 'ml']: {
          xs: 0,
          md: showSidebar ? `${navbarWidth}px` : 0,
        },
        transition: 'all 0.3s ease',
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 'xl',
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* Logo */}
        <Box
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={handleLogoClick}
        >
          <img
            src="/hawas-logo.png"
            alt="Menu Pro Logo"
            style={{ width: 60, height: 'auto' }}
          />
        </Box>

        {/* Center Navigation - HomePage Only & Not Authenticated */}
        {isHomePage && !isAuthenticated && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 4,
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <Button href="#services" sx={navLinkSx}>{t('nav.services')}</Button>
            <Button href="#about" sx={navLinkSx}>{t('nav.about')}</Button>
            <Button href="#ourClients" sx={navLinkSx}>{t('nav.partners')}</Button>
          </Box>
        )}

        {/* Desktop: Authenticated User Menu */}
        {isAuthenticated && user && isHomePage ? (
          <NavbarUserMenu
            user={user}
            anchorEl={anchorEl}
            isRTL={isRTL}
            t={t}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onDashboard={handleDashboard}
            onLogout={handleLogout}
          />
        ) : !isAuthenticated ? (
          <Button
            endIcon={<KeyboardArrowLeftRounded />}
            onClick={() => navigate(ROUTES.LOGIN)}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              color: 'black',
              px: 3,
              py: 1,
              fontSize: '1rem',
              fontWeight: 'bold',
              fontFamily: 'Cairo, Arabic, sans-serif',
            }}
          >
            {t('nav.login')}
          </Button>
        ) : isAuthenticated && isWaiter && !isHomePage ? (
          <IconButton
            onClick={handleLogout}
            sx={{
              color: '#ef4444',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(239, 68, 68, 0.08)',
              },
            }}
          >
            <Logout sx={{ fontSize: 22 }} />
          </IconButton>
        ) : null}

        {/* Mobile Menu Button */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
          {!isAuthenticated && (
            <Button
              onClick={() => navigate(ROUTES.LOGIN)}
              sx={{
                bgcolor: '#6523d1',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 500,
                fontFamily: 'Cairo, Arabic, sans-serif',
                textTransform: 'none',
                minWidth: 'auto',
                boxShadow: '0 2px 4px -1px rgba(101, 35, 209, 0.3)',
                '&:hover': { bgcolor: '#5019a8' },
              }}
            >
              {t('nav.loginShort')}
            </Button>
          )}

          {isAuthenticated && onMenuClick && (
            <IconButton
              onClick={onMenuClick}
              sx={{
                color: 'rgb(75, 85, 99)',
                p: 1,
                transition: 'all 0.2s ease-out',
                '&:hover': {
                  transform: 'scale(1.1)',
                  bgcolor: 'rgba(101, 35, 209, 0.05)',
                },
              }}
            >
              <MenuIcon sx={{ fontSize: 24 }} />
            </IconButton>
          )}
        </Box>
      </Toolbar>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && isAuthenticated && user && (
        <NavbarMobileMenu
          user={user}
          isRTL={isRTL}
          t={t}
          onDashboard={handleDashboard}
          onLogout={handleLogout}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}
    </AppBar>
  );
};

export default Navbar;
